/**
 * First-party consent prompt (doc 116 §7 D5).
 * Shown before first engagement or transcript processing.
 */

import React from 'react';
import { CAMLIN_COMPANION_ATTR } from './attributes';
import type { CompanionSiteConfig } from './types';

export interface CompanionConsentPromptProps {
  siteName: string;
  personaName: string;
  onAccept: () => void;
  onDecline: () => void;
}

const shellStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  zIndex: 10000,
  maxWidth: '360px',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: '14px',
  lineHeight: 1.5,
  color: '#0f172a',
  background: 'rgba(255,255,255,0.96)',
  border: '1px solid rgba(148,163,184,0.4)',
  borderRadius: '16px',
  boxShadow: '0 12px 40px rgba(15,23,42,0.18)',
  padding: '16px',
  backdropFilter: 'blur(8px)'
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  marginTop: '12px'
};

const primaryButtonStyle: React.CSSProperties = {
  flex: 1,
  border: 'none',
  borderRadius: '10px',
  padding: '10px 12px',
  background: '#4f46e5',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer'
};

const secondaryButtonStyle: React.CSSProperties = {
  flex: 1,
  border: '1px solid rgba(148,163,184,0.6)',
  borderRadius: '10px',
  padding: '10px 12px',
  background: 'transparent',
  color: '#334155',
  fontWeight: 600,
  cursor: 'pointer'
};

export function CompanionConsentPrompt({
  siteName,
  personaName,
  onAccept,
  onDecline
}: CompanionConsentPromptProps) {
  return (
    <div
      role="dialog"
      aria-labelledby="camlin-companion-consent-title"
      aria-describedby="camlin-companion-consent-body"
      {...{ [CAMLIN_COMPANION_ATTR.consent]: 'prompt' }}
      style={shellStyle}
    >
      <div id="camlin-companion-consent-title" style={{ fontWeight: 700, marginBottom: '8px' }}>
        Chat with {personaName}?
      </div>
      <p id="camlin-companion-consent-body" style={{ margin: 0, color: '#475569' }}>
        {personaName} on {siteName} can answer questions about this page. Conversations may be
        processed to improve your experience. You can dismiss anytime.
      </p>
      <div style={buttonRowStyle}>
        <button type="button" style={secondaryButtonStyle} onClick={onDecline}>
          Not now
        </button>
        <button type="button" style={primaryButtonStyle} onClick={onAccept}>
          Continue
        </button>
      </div>
    </div>
  );
}

export function buildConsentPromptProps(config: CompanionSiteConfig): Omit<
  CompanionConsentPromptProps,
  'onAccept' | 'onDecline'
> {
  return {
    siteName: config.siteName,
    personaName: config.persona.displayName
  };
}
